<template>
  <div class="w-full flex-1-0-auto header-height-top-space">
    <div class="w-full py-4 xl:pb-6">
      <div class="mb-8 md:px-6 lg:px-8 xl:px-20 xl:mb-12">
        <div class="container mb-8 xl:mb-12">
          <StepNavigation
            :step1="tr('pages.sales.steps.step1')"
            :step2="tr('pages.sales.steps.step2')"
            :step3="tr('pages.sales.steps.step3')"
            :active-step="2"
          />
        </div>
      </div>

      <div class="w-full viewport-gutter">
        <div class="container">
          <div class="text-sm xl:text-lg">
            <h1 class="pb-3 text-3xl font-bold leading-none xl:text-4xl font-lufthansa-head">
              {{ tr('pages.sales.checkout.title') }}
            </h1>
          </div>
        </div>
      </div>
    </div>

    <form
      class="w-full pt-6 pb-16 bg-gray-100 border-t border-gray-200 viewport-gutter xl:pt-12 xl:pb-24"
      @submit.prevent="onSubmit"
    >
      <div
        v-if="isLoading && !hasErrors"
        class="flex flex-col items-center justify-center h-full text-sm text-center xl:text-lg"
      >
        <span class="text-center loading-text">
          {{ tr('pages.sales.checkout.loading.title' ) }}
        </span>

        <div id="lh-loading" class="relative flex-shrink-0 w-full h-3 mt-4 bullets-9">
          <div id="lh-loading-1" class="absolute top-0 w-3 h-3 lh-loading" />
          <div id="lh-loading-2" class="absolute w-3 h-3 lh-loading" />
          <div id="lh-loading-3" class="absolute w-3 h-3 lh-loading" />
          <div id="lh-loading-4" class="absolute w-3 h-3 lh-loading" />
          <div id="lh-loading-5" class="absolute w-3 h-3 lh-loading" />
          <div id="lh-loading-6" class="absolute w-3 h-3 lh-loading" />
          <div id="lh-loading-7" class="absolute w-3 h-3 lh-loading" />
          <div id="lh-loading-8" class="absolute w-3 h-3 lh-loading" />
          <div id="lh-loading-9" class="absolute w-3 h-3 lh-loading" />
        </div>
        <p class="max-w-sm pt-6 mx-auto text-center xl:pt-12">
          {{ tr('pages.sales.checkout.loading.description' ) }}
        </p>
      </div>

      <div
        v-show="!isLoading"
        class="container xl:flex xl:justify-between"
      >
        <div class="w-full xl:max-w-209 xl:mr-5">
          <p class="leading-none pb-2 text-lg xl:text-2.5xl xl:pb-4 font-lufthansa-head">
            {{ tr('pages.sales.checkout.form.personal.title' ) }}
          </p>

          <div id="payment-personal-data" class="w-full py-3 mb-8 bg-white border border-gray-300 xl:py-5 xl:mb-16">
            <div class="px-3 xl:px-5">
              <div class="flex flex-col md:flex-row md:justify-between">
                <TextInput
                  v-model.trim="formFields.billingFirstName"
                  name="first-name"
                  :label="tr('components.flightVoucher.form.firstName.name')"
                  :placeholder="tr('components.flightVoucher.form.firstName.placeholder')"
                  :error="$v.formFields.billingFirstName.$error ? tr('errors.type.required') : ''"
                  extra-class="w-full mb-4 md:w-1/2 md:mr-10 xl:mr-20 xl:mb-6"
                  autocomplete="given-name"
                  @change="updateDefaults()"
                />
                <TextInput
                  v-model.trim="formFields.billingLastName"
                  name="last-name"
                  :label="tr('components.flightVoucher.form.lastName.name')"
                  :placeholder="tr('components.flightVoucher.form.lastName.placeholder')"
                  :error="$v.formFields.billingLastName.$error ? tr('errors.type.required') : ''"
                  extra-class="mb-4 md:w-1/2 xl:mb-6"
                  autocomplete="family-name"
                  @change=" updateDefaults()"
                />
              </div>

              <div class="flex flex-col md:flex-row md:justify-between">
                <TextInput
                  v-model.trim="formFields.email"
                  name="email"
                  :label="tr('components.flightVoucher.form.email.name')"
                  :placeholder="tr('components.flightVoucher.form.email.placeholder')"
                  :error="$v.formFields.email.$error ? tr(`errors.type.${emailErrorType}`) : ''"
                  extra-class="w-full mb-4 md:w-1/2 md:mr-10 xl:mr-20 xl:mb-6"
                  autocomplete="email"
                  @change="updateDefaults();"
                />
                <TextInput
                  v-model.trim="formFields.emailConfirmation"
                  name="email-confirmation"
                  :label="tr('components.flightVoucher.form.emailConfirmation.name')"
                  :placeholder="tr('components.flightVoucher.form.emailConfirmation.placeholder')"
                  :error="$v.formFields.emailConfirmation.$error ? tr(`errors.type.${emailConfirmationErrorType}`) : ''"
                  extra-class="mb-4 md:w-1/2 xl:mb-6"
                  autocomplete="email"
                />
              </div>

              <div class="flex flex-col md:flex-row md:justify-between">
                <TextInput
                  v-model.trim="formFields.street"
                  name="street"
                  :error="$v.formFields.street.$error ? tr('errors.type.required') : ''"
                  :label="tr('pages.sales.checkout.form.personal.street.name')"
                  :placeholder="tr('pages.sales.checkout.form.personal.street.placeholder')"
                  extra-class="w-full mb-4 md:w-1/2 md:mr-10 xl:mr-20 xl:mb-6"
                  autocomplete="address-line1"
                />
                <TextInput
                  v-model.trim="formFields.zipCode"
                  name="zipCode"
                  :error="$v.formFields.zipCode.$error ? tr(`errors.type.required`) : ''"
                  extra-class="w-full mb-4 md:w-1/2 xl:mb-6"
                  :label="tr('pages.sales.checkout.form.personal.zip.name')"
                  :placeholder="tr('pages.sales.checkout.form.personal.zip.placeholder')"
                  autocomplete="postal-code"
                />
              </div>
              <div class="flex flex-col md:flex-row md:justify-between">
                <TextInput
                  v-model.trim="formFields.city"
                  name="city"
                  :error="$v.formFields.city.$error ? tr('errors.type.required') : ''"
                  :label="tr('pages.sales.checkout.form.personal.city.name')"
                  :placeholder="tr('pages.sales.checkout.form.personal.city.placeholder')"
                  extra-class="w-full mb-4 md:w-1/2 md:mr-10 xl:mr-20 xl:mb-6"
                  autocomplete="address-level2"
                />
                <TextInput
                  v-model="formFields.country"
                  name="country"
                  :error="$v.formFields.country.$error ? tr('errors.type.required') : ''"
                  :label="tr('pages.sales.checkout.form.personal.country.name')"
                  :placeholder="tr('pages.sales.checkout.form.personal.country.placeholder')"
                  extra-class="w-full mb-4 md:w-1/2 xl:mb-6"
                  autocomplete="country"
                />
              </div>
            </div>
          </div>

          <div v-show="hasErrors" ref="error">
            <Alert
              v-for="error in errors"
              :key="error"
              :title="tr(`errors.type.${error}.title`)"
              :message="tr(`errors.type.${error}.description`)"
            />
          </div>

          <p class="leading-none pb-2 text-lg xl:text-2.5xl xl:pb-4 font-lufthansa-head">
            {{ tr('pages.sales.checkout.form.payment.title') }}
          </p>

          <div id="payment-datatrans-form" class="w-full p-3 mb-8 bg-white border border-gray-300 xl:p-5">
            <!--
              This data is required for 3DS, see https://docs.datatrans.ch/docs/3d-secure#mandatory-data-for-3ds-processing
              This data must be asked before we can initialize the payment intent
            -->
            <Card3dsInformationForm
              :locked="cardholderLocked"
              :default-name="formFields.cardholder.name || defaultCardholderName"
              :default-email="formFields.cardholder.email || defaultCardholderEmail"
              @unlock="onUnlock"
              @change="onChangeCardholder"
            />

            <div v-show="cardholderLocked && secureFieldsReady" class="flex flex-col md:flex-row md:justify-between">
              <div
                class="w-full mb-4 input-component-secure-field md:w-1/2 md:mr-10 xl:mr-20 xl:mb-6"
                :class="{ 'is-active': secureFieldsStatus.cardNumber.focused || secureFieldsStatus.cardNumber.filled, 'is-error': shouldCardNumberDisplayError}"
              >
                <label class="px-3 text-xs text-gray-600 cursor-text">
                  {{ tr('pages.sales.checkout.form.payment.cardNumber.name') }}
                </label>
                <div class="relative input">
                  <div id="card-number" />
                </div>

                <p v-if="shouldCardNumberDisplayError" class="error">
                  <span class="px-2 py-px text-xs leading-none text-white input-error bg-lh_error">{{ tr(`errors.type.invalid`) }}</span>
                </p>
              </div>

              <div class="flex justify-between md:w-1/2">
                <TextInput
                  v-model="formFields.expiryDate"
                  v-mask="'##/##'"
                  :error="$v.formFields.expiryDate.$error ? tr(`errors.type.${expiryDateErrorType}`) : ''"
                  :label="tr('pages.sales.checkout.form.payment.expiryDate.name')"
                  :placeholder="tr('pages.sales.checkout.form.payment.expiryDate.placeholder')"
                  extra-class="w-8/12 mb-4 mr-5 xl:mb-6"
                />

                <div
                  class="w-full input-component-secure-field"
                  :class="{ 'is-active': secureFieldsStatus.cvv.focused || secureFieldsStatus.cvv.filled, 'is-error': shouldCvvDisplayError }"
                >
                  <label class="px-3 text-xs text-gray-600 cursor-text">
                    {{ tr('pages.sales.checkout.form.payment.cvv.name') }}
                  </label>
                  <div class="relative input">
                    <div id="cvv-number" />
                  </div>

                  <p v-if="shouldCvvDisplayError" class="error">
                    <span class="px-2 py-px text-xs leading-none text-white input-error bg-lh_error">{{ tr('errors.type.invalid') }}</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="flex-shrink-0 w-full payment-summary xl:w-102 xl:flex">
          <ScrollWagon
            class="xl:w-102"
            top-limit-id="payment-personal-data"
            bottom-limit-id="payment-datatrans-form"
            :offset="120"
          >
            <p class="pb-2 text-lg leading-none xl:hidden">
              {{ tr('components.sales.summary.title') }}
            </p>

            <VoucherAmountSummary class="mb-6" :extended="true" />

            <div class="mb-10">
              <label class="flex cursor-pointer">
                <input
                  v-model="formFields.terms"
                  name="terms"
                  type="checkbox"
                  class="w-6 h-6 border-2 border-gray-600 form-checkbox focus:shadow-glow-ring-primary text-primary"
                  :class="{ 'border-red is-error': $v.formFields.terms.$error }"
                >
                <i18n path="pages.sales.checkout.terms" class="ml-3 leading-tight xl:text-lg" tag="p">
                  <template #link>
                    <router-link
                      class="underline"
                      :to="$i18nTo({ name: 'termsOfUse'})"
                      target="_blank"
                    >{{ tr('pages.sales.checkout.termsLink') }}</router-link>
                  </template>
                </i18n>
              </label>

              <i18n path="pages.sales.checkout.terms.privacyPolicy" class="ml-9 xl:text-lg" tag="p">
                <template #link>
                  <a
                    class="underline"
                    target="_blank"
                    :href="privacyLink"
                  >{{ tr('pages.sales.checkout.terms.privacyPolicyLink') }}</a>
                </template>
              </i18n>
            </div>

            <div v-if="$v.formFields.terms.$error" class="mb-10 -mt-5 text-red">
              <span
                class="inline-flex px-2 py-1 mt-2 text-xs leading-normal text-au_error input-error bg-au_error-100"
              >
                {{ tr('pages.sales.checkout.termsPrivacyError') }}
              </span>
            </div>

            <div class="relative w-full xl:max-w-xs">
              <Button
                variant="secondary"
                :loading="isLoading"
                :disabled="!cardholderLocked || !secureFieldsReady"
                full-width
                type="submit"
              >
                {{ tr('pages.sales.checkout.button.purchase') }}
              </Button>
            </div>
          </ScrollWagon>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import VoucherSaleCheckout from '~brand-pages/base/voucher-sale-checkout'
import TextInput from '~brand-components/lufthansa/shared/text-input.vue'
import StepNavigation from '~brand-components/lufthansa/navigation/step-navigation.vue'
import Alert from '~brand-components/lufthansa/alert/alert.vue'
import Button from '~brand-components/lufthansa/button/button.vue'
import VoucherAmountSummary from '~brand-components/lufthansa/sale/voucher-amount-summary.vue'
import brandConfig from '~branding/lufthansa/config'
import Card3dsInformationForm from '~brand-components/lufthansa/sale/card-3ds-information-form.vue'

const SECURE_FIELDS_STYLES = {
  '*': `
    width: calc(100% - 1.2rem);
    font-size: 1.125rem;
    color: #666666;
    background-color: transparent;
    height: 2.5rem;
    border-bottom-width: 2px;
    border-color: #666666;
    border-radius: 0;
    border-top-width: 0;
    border-left-width: 0;
    border-right-width: 0;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    border-style: solid;
    margin: 0.6rem;
    outline: 0;
  `,
  '*:focus': `
    box-shadow: 0 0 0.6rem 0.1rem #52627c;
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  `,
}

export default VoucherSaleCheckout.extend({
  components: {
    Card3dsInformationForm,
    TextInput,
    StepNavigation,
    Alert,
    Button,
    VoucherAmountSummary,
  },
  computed: {
    brandConfig() {
      return brandConfig
    },
    secureFieldStyles() {
      return SECURE_FIELDS_STYLES
    },
    purchase() {
      return this.$store.getters['sales/purchase']
    },
    privacyLink() {
      if (this.$i18n.locale === 'de') {
        return 'https://www.lufthansa.com/de/de/datenschutz'
      }

      return 'https://www.lufthansa.com/de/en/data-protection'
    },
  },
})
</script>

<style lang="scss" scoped>
.border-red {
  border-color: red;

  &.border-gray-600 {
    border-color: red;
  }
}

.input-component-secure-field {
  label {
    @apply opacity-0;

    opacity: 0;
    margin-left: -0.6rem;
  }

  .input {
    height: 2.5rem;
    margin-left: -0.6rem;
    margin-bottom: 1.5rem;

    &::before {
      position: absolute;
      right: 0;
      bottom: 0;
      width: 2px;
      height: 50%;
      margin-bottom: -0.6rem;
      content: '';
      background: #666;

      @apply bg-gray-600;
    }

    > div {
      margin-top: -0.6rem;
      margin-right: -0.6rem;
    }
  }

  &.is-active {
    label {
      @apply opacity-100;

      opacity: 1;
    }

    .input {
      &::before {
        display: none;
      }
    }
  }

  &.is-error {
    label {
      @apply opacity-100;

      opacity: 1;
    }

    .error {
      margin-top: -0.9rem;
    }

    .input {
      input {
        border-color: #c00;
      }

      &::before {
        display: none;
      }
    }
  }
}

@screen xl {
  .payment-summary {
    padding-top: 42px;
    padding-bottom: 31px;
  }
}
</style>
